import type {
   CreateAssignmentPayload,
   CreateAssignmentResponse,
} from "@laborchart-modules/lc-core-api/dist/api/assignments/create-assignment";
import type { CreateEquipmentAssignmentPayload } from "@laborchart-modules/lc-core-api/dist/api/equipment-assignment/create-equipment-assignment";
import type { GetEquipmentAssignmentDetailResponse } from "@laborchart-modules/lc-core-api/dist/api/equipment-assignment/get-equipment-assignment";
import type {
   UpdateEquipmentAssignmentPayload,
   UpdateSingleEquipmentAssignmentResponse,
} from "@laborchart-modules/lc-core-api/dist/api/equipment-assignment/update-equipment-assignment";
import type { StoreJsonResponse } from "./common";
import { Store } from "@/stores/common/store.core";
import type { GetEquipmentAssignmentScheduleResponse } from "@laborchart-modules/lc-core-api/dist/api/equipment-assignment/get-equipment-assignment-schedules";
import type { DeleteEquipmentAssignmentResponse } from "@laborchart-modules/lc-core-api/dist/api/equipment-assignment/delete-equipment-assignment";
import type { SerializedEquipmentAssignment } from "@laborchart-modules/common/dist/postgres/serializers/equipment-assignment-serializer";
import type { ResponsePayload } from "@laborchart-modules/common/dist/api";

// Defined locally until lc-core-api publishes a new version with batch equipment assignment types
export type BatchCreateEquipmentAssignmentsPayload = Omit<
   CreateEquipmentAssignmentPayload,
   "resource_id"
> & { resource_ids: string[] };

export abstract class EquipmentAssignmentStore extends Store {
   static createEquipmentAssignment(
      payload: CreateAssignmentPayload,
   ): StoreJsonResponse<CreateAssignmentResponse> {
      return this.requestJson({
         body: payload,
         method: "POST",
         url: "/api/v3/equipment-assignments",
      });
   }

   static batchCreateEquipmentAssignments(
      payload: BatchCreateEquipmentAssignmentsPayload,
   ): StoreJsonResponse<ResponsePayload<SerializedEquipmentAssignment[]>> {
      return this.requestJson({
         body: payload,
         method: "POST",
         url: "/api/v3/equipment-assignments/batch-create",
      });
   }

   static getEquipmentAssignmentDetails(
      equipmentAssignmentId: string,
   ): StoreJsonResponse<GetEquipmentAssignmentDetailResponse> {
      return this.requestJson({
         url: `/api/v3/equipment-assignments/${equipmentAssignmentId}`,
         method: "GET",
      });
   }

   static updateSingleEquipmentAssignment({
      equipmentAssignmentId,
      update,
   }: {
      equipmentAssignmentId: string;
      update: UpdateEquipmentAssignmentPayload;
   }): StoreJsonResponse<UpdateSingleEquipmentAssignmentResponse> {
      return this.requestJson({
         url: `/api/v3/equipment-assignments/${equipmentAssignmentId}`,
         method: "PATCH",
         body: update,
      });
   }

   static getEquipmentAssignmentSchedules(
      equipmentResourceId: string,
   ): StoreJsonResponse<GetEquipmentAssignmentScheduleResponse> {
      return this.requestJson({
         url: `/api/v3/equipment-assignments-schedules/${equipmentResourceId}`,
         method: "GET",
      });
   }

   static deleteEquipmentAssignment(
      equipmentAssignmentId: string,
   ): StoreJsonResponse<DeleteEquipmentAssignmentResponse> {
      return this.requestJson({
         url: `/api/v3/equipment-assignments/${equipmentAssignmentId}`,
         method: "DELETE",
      });
   }
}
